import { Document, Page, Text, View } from "@react-pdf/renderer";
import { getTenantStore } from "../../../../stores/tenantStore";
import {
	formatDateDdMmmYyyy,
	formatDateTimeUtc,
} from "../../../../utils/date_formatter";
import {
	getProbabilityScore,
	getRiskFrequencyDescription,
	getSeverityDescription,
} from "../../../../utils/riskMatrix";
import { capitalizeFirstLetter } from "../../../../utils/string_convert_helper";
import Tag from "../../ReactPdfComponents/ReactPdfTag";
import { styles } from "../../ReactPdfStyles";
import PdfHeader from "../PdfHeader";
import ReportingProjectDetailSection from "../ReportingProjectDetailSection";
import RiskMatrixSection from "../RiskMatrixSection";
import { formatResponsiblePerson, renderApprovedOn } from "../utils";

export default function NonConformancePDFTemplate({
	data,
}: {
	data: Record<string, any>;
}) {
	const tenantList = getTenantStore().getTenantList();
	const tenant = tenantList.find(
		(tenant: any) => tenant.value === data.operator
	);
	const reportLogoUrl = tenant?.report_logo ?? tenant?.logo;

	return (
		<Document>
			<Page size="A4" style={styles.document} orientation="portrait">
				<PdfHeader
					reportTitle="NON-CONFORMANCE REPORT"
					data={{
						internal_report: data.internal_report,
						report_number: data.report_number,
						title: data.title,
					}}
					reportLogo={reportLogoUrl}
				/>

				{data?.is_project_related ? (
					<ReportingProjectDetailSection data={data} />
				) : (
					<View style={[styles.section, styles.tableFontSize]}>
						<Text style={styles.tableHeader}>Project detail</Text>
						<View style={styles.row}>
							<View style={{ width: "100%", flexDirection: "column" }}>
								<View style={styles.row}>
									<Text style={styles.labelCell}>Tenant</Text>
									<Text style={[styles.valueCell, styles.borderRight]}>
										{data?.operator ?? "N/A"}
									</Text>
								</View>
							</View>
						</View>
					</View>
				)}
				<NCRDetailSection
					submitted_on={formatDateDdMmmYyyy(data?.submitted_on ?? "N/A")}
					date_expired={formatDateDdMmmYyyy(data?.date_expired ?? "N/A")}
					department_name={data?.department_name ?? "N/A"}
					ncr_type={data?.ncr_type ?? "N/A"}
					requirements={data?.requirements ?? "N/A"}
					description={data?.description ?? "N/A"}
				/>
				<ApprovalRecordSection
					created_by={data?.author_name ?? "N/A"}
					approved_by={data?.reviewer_name ?? "N/A"}
					created_on={formatDateTimeUtc(data?.created_at ?? "N/A")}
					approved_on={renderApprovedOn(data)}
				/>
				<View break style={[styles.section, styles.tableFontSize]}>
					<Text style={styles.tableHeader}>Potential risks</Text>
					<View style={{ marginTop: 16 }}>
						<PotentialRiskTable potential_risks={data?.potential_risks ?? []} />
					</View>
				</View>
				<ActionsSection
					actions={data.actions ?? data.follow_up_actions ?? []}
				/>
			</Page>

			<Page size="A4" style={styles.documentTight} orientation="landscape">
				<PdfHeader
					reportTitle="NON-CONFORMANCE REPORT"
					data={{
						internal_report: data.internal_report,
						report_number: data.report_number,
						title: data.title,
					}}
				/>
				<RiskMatrixSection />
			</Page>
		</Document>
	);
}

type NCRDetailSectionTypes = {
	submitted_on: string;
	date_expired: string;
	department_name: string;
	ncr_type: string;
	requirements: string;
	description: string;
};

function NCRDetailSection({
	submitted_on,
	date_expired,
	department_name,
	ncr_type,
	requirements,
	description,
}: NCRDetailSectionTypes) {
	return (
		<View style={[styles.section, styles.tableFontSize]}>
			<Text style={styles.tableHeader}>NCR detail</Text>
			<View style={styles.column}>
				<View style={styles.row}>
					<Text style={styles.labelCell}>Date originated</Text>
					<Text style={styles.valueCell}>{submitted_on ?? "N/A"}</Text>
					<Text style={styles.labelCell}>Date expiry</Text>
					<Text style={[styles.valueCell, styles.borderRight]}>
						{date_expired ?? "N/A"}
					</Text>
				</View>
				<View style={styles.row}>
					<Text style={styles.labelCell}>Department</Text>
					<Text style={styles.valueCell}>{department_name ?? "N/A"}</Text>
					<Text style={styles.labelCell}>NCR type</Text>
					<Text style={[styles.valueCell, styles.borderRight]}>
						{capitalizeFirstLetter(ncr_type ?? "N/A")}
					</Text>
				</View>
				<View style={styles.row}>
					<Text style={styles.labelCell}>Requirement</Text>
					<Text style={[styles.valueCell, styles.borderRight]}>
						{requirements ?? "N/A"}
					</Text>
				</View>
				<View style={styles.row}>
					<Text style={styles.labelCell}>Brief description</Text>
					<Text style={[styles.valueCell, styles.borderRight]}>
						{description ?? "N/A"}
					</Text>
				</View>
			</View>
		</View>
	);
}

type ApprovalRecordSectionTypes = {
	created_by: string;
	approved_by: string;
	created_on: string;
	approved_on: string | React.ReactNode;
};

function ApprovalRecordSection({
	created_by,
	approved_by,
	created_on,
	approved_on,
}: ApprovalRecordSectionTypes) {
	return (
		<View style={[styles.section, styles.tableFontSize]}>
			<Text style={styles.tableHeader}>Approval record</Text>
			<View style={styles.column}>
				<View style={styles.row}>
					<Text style={styles.labelCell}>NCR created by</Text>
					<Text style={styles.valueCell}>{created_by ?? "N/A"}</Text>

					<Text style={styles.labelCell}>Approved by</Text>
					<Text style={[styles.valueCell, styles.borderRight]}>
						{approved_by ?? "N/A"}
					</Text>
				</View>
				<View style={styles.row}>
					<Text style={styles.labelCell}>NCR created on</Text>
					<Text style={styles.valueCell}>{created_on ?? "N/A"}</Text>
					<Text style={styles.labelCell}>Approved on</Text>
					<Text style={[styles.valueCell, styles.borderRight]}>
						{approved_on ?? "N/A"}
					</Text>
				</View>
			</View>
		</View>
	);
}

type PotentialRiskType = {
	risk_number: string;
	consequence: string;
	severity_level: string;
	probability: string;
};
type PotentialRiskTableTypes = {
	potential_risks: PotentialRiskType[];
};

function PotentialRiskTable({ potential_risks }: PotentialRiskTableTypes) {
	const riskBgColor = {
		HIGH: "#FDECEC",
		MEDIUM: "#FFF3D9",
		LOW: "#EAF7EE",
	};

	const riskTextColor = {
		HIGH: "#DC2626",
		MEDIUM: "#F59E0B",
		LOW: "#16A34A",
	};

	return (
		<View style={[styles.tableFontSize]}>
			<View style={[styles.row]}>
				<Text
					style={[styles.borderTop, styles.borderLeft, styles.tableHeaderRow]}
				>
					Risk no.
				</Text>
				<Text
					style={[styles.borderTop, styles.borderLeft, styles.tableHeaderRow]}
				>
					Consequence
				</Text>
				<Text
					style={[styles.borderTop, styles.borderLeft, styles.tableHeaderRow]}
				>
					Severity
				</Text>
				<Text
					style={[styles.borderTop, styles.borderLeft, styles.tableHeaderRow]}
				>
					Probability
				</Text>
				<Text
					style={[
						styles.borderTop,
						styles.borderLeft,
						styles.borderRight,
						styles.tableHeaderRow,
					]}
				>
					Risk
				</Text>
			</View>

			{potential_risks.length > 0 ? (
				potential_risks.map((riskItem: any) => {
					const { risk_number, consequence, severity_level, probability } =
						riskItem;

					const score = getProbabilityScore(severity_level, probability);
					const riskLevel = score?.split(" - ")[1];

					return (
						<View style={styles.row}>
							<Text
								style={[styles.borderTop, styles.borderLeft, styles.valueCell]}
							>
								{risk_number ?? "N/A"}
							</Text>
							<Text
								style={[styles.borderTop, styles.borderLeft, styles.valueCell]}
							>
								{consequence ?? "N/A"}
							</Text>
							<View
								style={[
									styles.borderTop,
									styles.borderLeft,
									styles.valueCell,
									{ justifyContent: "flex-start" },
								]}
							>
								{severity_level ? (
									<View
										style={{
											flexDirection: "row",
											alignItems: "flex-start",
											width: "100%",
										}}
									>
										<Tag>{severity_level}</Tag>
										{consequence && (
											<View
												style={{
													flexGrow: 1,
													flexBasis: 0,
													paddingLeft: 4,
													paddingTop: 3,
													lineHeight: 0.8,
												}}
											>
												<Text wrap>
													{getSeverityDescription(consequence, severity_level)}
												</Text>
											</View>
										)}
									</View>
								) : (
									"N/A"
								)}
							</View>
							<View
								style={[
									styles.borderTop,
									styles.borderLeft,
									styles.valueCell,
									{ justifyContent: "flex-start" },
								]}
							>
								{probability ? (
									<View
										style={{
											flexDirection: "row",
											alignItems: "flex-start",
											width: "100%",
										}}
									>
										<Tag>{probability}</Tag>
										<View
											style={{
												flexGrow: 1,
												flexBasis: 0,
												paddingLeft: 4,
												paddingTop: 3,
												lineHeight: 0.8,
											}}
										>
											<Text wrap>
												{getRiskFrequencyDescription(Number(probability))}
											</Text>
										</View>
									</View>
								) : (
									"N/A"
								)}
							</View>
							<View
								style={[
									styles.borderTop,
									styles.borderLeft,
									styles.borderRight,
									styles.valueCell,
									{
										backgroundColor:
											riskBgColor[riskLevel as keyof typeof riskBgColor],
									},
								]}
							>
								<View
									style={{
										color:
											riskTextColor[riskLevel as keyof typeof riskTextColor],
										fontWeight: "bold",
									}}
								>
									<Text>{score}</Text>
								</View>
							</View>
						</View>
					);
				})
			) : (
				<View style={styles.row}>
					<Text
						style={[
							styles.valueCell,
							styles.borderLeft,
							styles.borderRight,
							{ textAlign: "center" },
						]}
					>
						No potential risks available
					</Text>
				</View>
			)}
		</View>
	);
}

function ActionsSection({ actions }: { actions: any[] }) {
	const renderCompletedBy = (action: any) => {
		const responsibleDisplay = formatResponsiblePerson(
			action?.responsible,
			action?.responsible_name
		);
		const completed = action.completed_on
			? `${formatDateTimeUtc(action.completed_on as any)}`
			: null;
		return (
			<View style={styles.column}>
				{responsibleDisplay && (
					<View style={{ marginBottom: 8 }}>
						<Text
							style={{
								fontWeight: "bold",
							}}
							wrap={false}
						>
							Person Responsible:
						</Text>
						<Text>{responsibleDisplay}</Text>
					</View>
				)}
				{completed ? (
					<View style={{ marginBottom: 8 }}>
						<Text style={{ fontWeight: "bold" }}>Completed by:</Text>
						<Text>{`${responsibleDisplay} (${completed})`}</Text>
					</View>
				) : (
					<Text style={{ fontStyle: "italic" }}>Pending completion</Text>
				)}

				{/* Spacing */}
				<View style={{ height: 6 }} />

				{/* Closing comment section */}
				{action.closing_comment && (
					<>
						<Text style={{ fontWeight: "bold" }}>Closing comment:</Text>
						<Text>{action.closing_comment}</Text>
					</>
				)}
			</View>
		);
	};
	return (
		<View style={[styles.section, styles.tableFontSize]}>
			<Text style={[styles.tableHeader, { marginBottom: 15 }]}>Actions</Text>
			<View style={styles.row}>
				<Text
					style={[
						styles.borderTop,
						styles.borderLeft,
						{
							flex: 1,
							fontWeight: "bold",
							backgroundColor: "#F2F2F2",
							padding: 8,
						},
					]}
				>
					Action no.
				</Text>
				<Text
					style={[
						styles.borderTop,
						styles.borderLeft,
						{
							flex: 1,
							fontWeight: "bold",
							backgroundColor: "#F2F2F2",
							padding: 8,
						},
					]}
				>
					Description
				</Text>
				<Text
					style={[
						styles.borderTop,
						styles.borderLeft,
						styles.borderRight,
						{
							flex: 1,
							fontWeight: "bold",
							backgroundColor: "#F2F2F2",
							padding: 8,
						},
					]}
				>
					Remarks
				</Text>
			</View>
			<View>
				{Array.isArray(actions) && actions.length > 0 ? (
					actions.map((action: any) => (
						<View key={action.action_number} style={styles.row}>
							<Text style={[styles.valueCell, styles.borderLeft]}>
								{action.action_number ?? "—"}
							</Text>
							<Text style={[styles.valueCell, styles.borderLeft]}>
								{action.description ?? "—"}
							</Text>
							<View
								style={[
									styles.valueCell,
									styles.borderLeft,
									styles.borderRight,
								]}
							>
								{renderCompletedBy(action)}
							</View>
						</View>
					))
				) : (
					<View style={styles.row}>
						<Text
							style={[
								styles.valueCell,
								styles.borderLeft,
								styles.borderRight,
								{ textAlign: "center" },
							]}
						>
							No actions available
						</Text>
					</View>
				)}
			</View>
		</View>
	);
}
